import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./sidebar";
import { LuCopy, LuExternalLink, LuPencil, LuSparkles, LuTrash2, LuTicket } from "react-icons/lu";
import "../styles/coupons.css";
import { apiRequest, publicApiGet } from "./api";

function formatExpiry(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const emptyForm = () => ({
  store_name: "",
  store_domain: "",
  code: "",
  discount_label: "",
  fine_print: "",
  expires_at: "",
});

export default function Coupons() {
  const navigate = useNavigate();
  const [shopStores, setShopStores] = useState([]);
  const [curated, setCurated] = useState([]);
  const [curatedLoading, setCuratedLoading] = useState(true);
  const [topToday, setTopToday] = useState([]);
  const [topTodayLoading, setTopTodayLoading] = useState(true);
  const [aiStoreQuery, setAiStoreQuery] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [q, setQ] = useState("");
  const [filterStore, setFilterStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  /** Per AI card: sending | ok | err — so feedback buttons feel responsive and errors surface. */
  const [aiFeedbackState, setAiFeedbackState] = useState({});
  const readyForSearch = useRef(false);

  const loadShopStores = useCallback(async () => {
    const names = await apiRequest("/api/coupons/shop-stores");
    setShopStores(Array.isArray(names) ? names : []);
  }, []);

  const fetchCoupons = useCallback(async (searchQ, storeFilter) => {
    const params = new URLSearchParams();
    if (searchQ.trim()) params.set("q", searchQ.trim());
    if (storeFilter.trim()) params.set("store", storeFilter.trim());
    const qs = params.toString();
    const path = qs ? `/api/coupons?${qs}` : "/api/coupons";
    const rows = await apiRequest(path);
    setCoupons(Array.isArray(rows) ? rows : []);
  }, []);

  const loadCurated = useCallback(async () => {
    setCuratedLoading(true);
    try {
      const rows = await publicApiGet("/api/public/curated-coupons");
      setCurated(Array.isArray(rows) ? rows : []);
    } catch {
      setCurated([]);
    } finally {
      setCuratedLoading(false);
    }
  }, []);

  const loadTopToday = useCallback(async () => {
    setTopTodayLoading(true);
    try {
      const rows = await publicApiGet("/api/public/coupons/top-today");
      setTopToday(Array.isArray(rows) ? rows : []);
    } catch {
      setTopToday([]);
    } finally {
      setTopTodayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        await Promise.all([loadShopStores(), fetchCoupons("", ""), loadCurated(), loadTopToday()]);
        readyForSearch.current = true;
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load coupons.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, loadShopStores, fetchCoupons, loadCurated, loadTopToday]);

  useEffect(() => {
    if (!readyForSearch.current) return;
    const id = window.setTimeout(() => {
      fetchCoupons(q, filterStore).catch((e) => setError(e.message || "Search failed."));
    }, 320);
    return () => window.clearTimeout(id);
  }, [q, filterStore, fetchCoupons]);

  const storeOptions = useMemo(() => {
    const fromCoupons = [...new Set(coupons.map((c) => c.store_name).filter(Boolean))];
    const merged = new Set([...shopStores, ...fromCoupons]);
    return [...merged].sort((a, b) => a.localeCompare(b));
  }, [shopStores, coupons]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const store_name = form.store_name.trim();
    const code = form.code.trim();
    if (!store_name || !code) {
      setError("Store name and coupon code are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        store_name,
        store_domain: form.store_domain.trim() || null,
        code,
        discount_label: form.discount_label.trim() || null,
        fine_print: form.fine_print.trim() || null,
        expires_at: form.expires_at ? form.expires_at : null,
      };
      if (editingId != null) {
        await apiRequest(`/api/coupons/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      } else {
        await apiRequest("/api/coupons", {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      setForm(emptyForm());
      setEditingId(null);
      await Promise.all([fetchCoupons(q, filterStore), loadShopStores()]);
    } catch (err) {
      setError(err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.coupon_id);
    setForm({
      store_name: c.store_name || "",
      store_domain: c.store_domain || "",
      code: c.code || "",
      discount_label: c.discount_label || "",
      fine_print: c.fine_print || "",
      expires_at: c.expires_at ? String(c.expires_at).slice(0, 10) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this coupon from your list?")) return;
    setError("");
    try {
      await apiRequest(`/api/coupons/${id}`, { method: "DELETE" });
      await fetchCoupons(q, filterStore);
    } catch (err) {
      setError(err.message || "Could not delete.");
    }
  };

  const recordCouponFeedback = useCallback(async (payload) => {
    await apiRequest("/api/coupons/feedback", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }, []);

  /** Optional analytics on copy; never block copy or show errors. */
  const recordCopyFeedback = useCallback(
    async (payload) => {
      try {
        await recordCouponFeedback(payload);
      } catch {
        /* optional */
      }
    },
    [recordCouponFeedback]
  );

  const submitAiFeedback = useCallback(
    async (cardKey, payload) => {
      setError("");
      setAiFeedbackState((s) => ({ ...s, [cardKey]: "sending" }));
      try {
        await recordCouponFeedback(payload);
        setAiFeedbackState((s) => ({ ...s, [cardKey]: "ok" }));
        window.setTimeout(() => {
          setAiFeedbackState((s) => {
            const next = { ...s };
            delete next[cardKey];
            return next;
          });
        }, 4000);
      } catch (e) {
        setAiFeedbackState((s) => ({ ...s, [cardKey]: "err" }));
        setError(e.message || "Could not save feedback. Try signing in again.");
      }
    },
    [recordCouponFeedback]
  );

  const copyCode = async (id, code, meta = {}) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 2000);
      if (meta?.store_name) {
        recordCopyFeedback({
          store_name: meta.store_name,
          code,
          source: meta.source || "ai",
          outcome: "copied",
        });
      }
    } catch {
      setError("Could not copy — select the code manually.");
    }
  };

  const runAiSearch = async (e) => {
    if (e) e.preventDefault();
    const store = aiStoreQuery.trim();
    if (!store) {
      setAiMessage("Enter a store name first.");
      setAiSuggestions([]);
      return;
    }
    setAiLoading(true);
    setAiMessage("");
    try {
      const data = await apiRequest(`/api/coupons/ai-suggestions?store=${encodeURIComponent(store)}`);
      const rows = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setAiSuggestions(rows);
      setAiMessage(
        rows.length > 0
          ? (data?.explanation || "Ranked by recent usage and reliability.")
          : "No strong matches yet. Try a broader store name."
      );
    } catch (err) {
      setAiSuggestions([]);
      setAiMessage(err.message || "Could not run AI suggestions.");
    } finally {
      setAiLoading(false);
    }
  };

  const isExpired = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t < Date.now();
  };

  const storeHref = (domain) => {
    const d = String(domain || "").trim().replace(/^https?:\/\//i, "");
    if (!d) return null;
    return `https://${d}`;
  };

  return (
    <div className="dashboard-container">
      <Sidebar showExtension />
      <main className="coupons-main">
        <div className="coupons-hero-pill">
          <LuTicket size={14} aria-hidden />
          Coupons
        </div>
        <h1 className="coupons-title">Deals to try</h1>
        <p className="coupons-lede">
          Use <strong>Official store hubs</strong> for retailer-published offers, <strong>Most used today</strong> for
          what the community tried recently, and <strong>AI coupon finder</strong> for ranked code ideas — always
          confirm at checkout.
        </p>

        <section className="coupons-ai-card" aria-labelledby="ai-coupon-heading">
          <h2 id="ai-coupon-heading" className="coupons-section-title">
            <LuSparkles size={20} className="text-rose-500" aria-hidden />
            AI coupon finder
          </h2>
          <p className="coupons-section-sub">
            Enter a store name. We rank codes from <strong>your saves</strong>, <strong>anonymized saves</strong> from
            other Cart-It shoppers for that retailer, and <strong>feedback</strong> (copy / worked / didn’t work).
          </p>
          <form className="coupons-ai-row" onSubmit={runAiSearch}>
            <input
              className="coupons-input"
              value={aiStoreQuery}
              onChange={(e) => setAiStoreQuery(e.target.value)}
              placeholder="Try: target, nike, sephora..."
            />
            <button type="submit" className="coupons-btn-primary" disabled={aiLoading}>
              {aiLoading ? "Finding…" : "Find codes"}
            </button>
          </form>
          {aiMessage ? <p className="coupons-ai-message">{aiMessage}</p> : null}
          {aiSuggestions.length > 0 ? (
            <ul className="coupons-ai-grid">
              {aiSuggestions.map((s, idx) => {
                const cardKey = `${s.store_name}-${s.code}-${idx}`;
                const fb = aiFeedbackState[cardKey];
                return (
                  <li key={cardKey} className="coupons-card">
                    <span className="coupons-card-store">{s.store_name}</span>
                    <p className="coupons-card-code">{s.code}</p>
                    {s.discount_label ? <p className="coupons-card-discount">{s.discount_label}</p> : null}
                    <p className="coupons-ai-meta">
                      Score {s.score} · {(Number(s.success_rate_7d || 0) * 100).toFixed(0)}% success (7d)
                      {Number(s.save_count) > 0 ? ` · ${s.save_count} saved on Cart-It` : ""}
                    </p>
                    <div className="coupons-card-actions">
                      <button
                        type="button"
                        className="coupons-icon-btn"
                        disabled={fb === "sending"}
                        aria-label="Copy this promo code to your clipboard"
                        onClick={() =>
                          copyCode(`ai-${idx}`, s.code, {
                            store_name: s.store_name,
                            source: "ai",
                          })
                        }
                      >
                        <LuCopy size={14} aria-hidden />
                        {copiedId === `ai-${idx}` ? "Copied to clipboard" : "Copy to clipboard"}
                      </button>
                      <button
                        type="button"
                        className="coupons-icon-btn"
                        disabled={fb === "sending"}
                        aria-label="Record that this code worked at checkout to improve rankings"
                        onClick={() =>
                          submitAiFeedback(cardKey, {
                            store_name: s.store_name,
                            code: s.code,
                            source: "ai",
                            outcome: "used",
                          })
                        }
                      >
                        Mark worked at checkout
                      </button>
                      <button
                        type="button"
                        className="coupons-icon-btn border-red-100 text-red-700 hover:bg-red-50"
                        disabled={fb === "sending"}
                        aria-label="Record that this code failed at checkout to improve rankings"
                        onClick={() =>
                          submitAiFeedback(cardKey, {
                            store_name: s.store_name,
                            code: s.code,
                            source: "ai",
                            outcome: "failed",
                          })
                        }
                      >
                        Mark didn’t work at checkout
                      </button>
                    </div>
                    {fb === "sending" ? (
                      <p className="coupons-ai-feedback coupons-ai-feedback--pending">Saving your feedback…</p>
                    ) : null}
                    {fb === "ok" ? (
                      <p className="coupons-ai-feedback coupons-ai-feedback--ok">
                        Saved — we use this to rank suggestions for everyone.
                      </p>
                    ) : null}
                    {fb === "err" ? (
                      <p className="coupons-ai-feedback coupons-ai-feedback--err">Couldn’t save feedback — check above.</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>

        <section className="coupons-top-today" aria-labelledby="top-today-heading">
          <h2 id="top-today-heading" className="coupons-section-title">
            Most used today
          </h2>
          <p className="coupons-section-sub coupons-section-sub--tight">
            Built from real Cart-It activity in the last 24 hours: copies and “worked” / “didn’t work” taps on
            codes. Tap a chip to <strong>copy that code</strong> — it’s a popularity snapshot, not a guarantee the
            store will accept it.
          </p>
          {topTodayLoading ? (
            <p className="coupons-muted">Loading daily leaders…</p>
          ) : topToday.length === 0 ? (
            <p className="coupons-empty-inline">No usage data yet today.</p>
          ) : (
            <div className="coupons-top-list">
              {topToday.slice(0, 8).map((c, idx) => (
                <button
                  key={`${c.store_name}-${c.code}-${idx}`}
                  type="button"
                  className="coupons-top-chip"
                  title={`Copy ${c.code} to clipboard (${c.used_today} “worked” reports today)`}
                  aria-label={`Copy promo code ${c.code} for ${c.store_name} to clipboard`}
                  onClick={() =>
                    copyCode(`top-${idx}`, c.code, {
                      store_name: c.store_name,
                      source: "ai",
                    })
                  }
                >
                  <span className="coupons-top-rank">#{idx + 1}</span>
                  <span>{c.store_name}</span>
                  <span className="font-mono">{c.code}</span>
                  <span className="coupons-top-count">{c.used_today} used</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {error ? <div className="coupons-error">{error}</div> : null}

        <section className="coupons-curated-section" aria-labelledby="curated-heading">
          <h2 id="curated-heading" className="coupons-section-title">
            <LuSparkles size={20} className="text-rose-500" aria-hidden />
            Official store hubs
          </h2>
          <p className="coupons-section-sub">
            Promo codes expire constantly — instead of guessing, open each retailer’s official offers or loyalty
            page for codes that apply <em>today</em>.
          </p>
          {curatedLoading ? (
            <p className="coupons-muted">Loading offers…</p>
          ) : curated.length === 0 ? (
            <p className="coupons-empty-inline">No curated offers yet. Check back soon.</p>
          ) : (
            <ul className="coupons-curated-grid">
              {curated.map((c) => {
                const expired = isExpired(c.expires_at);
                const primaryHref =
                  (c.deals_url && String(c.deals_url).trim()) || storeHref(c.store_domain);
                const codeStr =
                  c.code != null && String(c.code).trim() ? String(c.code).trim() : "";
                return (
                  <li
                    key={c.id}
                    className={`coupons-curated-card${expired ? " coupons-card-expired" : ""}`}
                  >
                    <span className="coupons-card-store">{c.store_name}</span>
                    {c.source_site ? (
                      <span className="coupons-source">via {c.source_site}</span>
                    ) : null}
                    {codeStr ? (
                      <p className="coupons-card-code">{codeStr}</p>
                    ) : (
                      <p className="coupons-card-no-code">
                        No single promo code here — apply offers from the retailer’s official page at checkout.
                      </p>
                    )}
                    {c.discount_label ? (
                      <p className="coupons-card-discount">{c.discount_label}</p>
                    ) : null}
                    {c.fine_print ? (
                      <p className="coupons-fine-print">{c.fine_print}</p>
                    ) : null}
                    <div className="coupons-card-meta">
                      {c.expires_at ? (
                        <span className={expired ? "font-semibold text-red-600" : ""}>
                          {expired ? "Expired " : "Expires "}
                          {formatExpiry(c.expires_at)}
                        </span>
                      ) : (
                        <span className="text-slate-500">Expiry varies by offer — check on site</span>
                      )}
                    </div>
                    <div className="coupons-card-actions">
                      {codeStr ? (
                        <button
                          type="button"
                          className="coupons-icon-btn"
                          aria-label="Copy this promo code to your clipboard"
                          onClick={() =>
                            copyCode(c.id, codeStr, {
                              store_name: c.store_name,
                              source: "curated",
                            })
                          }
                        >
                          <LuCopy size={14} aria-hidden />
                          {copiedId === c.id ? "Copied to clipboard" : "Copy to clipboard"}
                        </button>
                      ) : null}
                      {primaryHref ? (
                        <a
                          href={primaryHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="coupons-icon-btn coupons-icon-link coupons-official-link"
                          aria-label={`Open official offers for ${c.store_name} in a new tab`}
                        >
                          <LuExternalLink size={14} aria-hidden />
                          Open official offers
                        </a>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <details className="coupons-personal-details">
          <summary>Your saved codes — add or manage your own</summary>
          <div className="coupons-personal-inner">
            <section className="coupons-form-card" aria-labelledby="coupon-form-heading">
              <h2 id="coupon-form-heading" className="coupons-form-card-title">
                {editingId != null ? "Edit coupon" : "Add a coupon"}
              </h2>
              <form onSubmit={onSubmit}>
                <div className="coupons-form-grid">
                  <label className="coupons-field">
                    <span className="coupons-label">Store name *</span>
                    <input
                      className="coupons-input"
                      value={form.store_name}
                      onChange={(e) => setForm((f) => ({ ...f, store_name: e.target.value }))}
                      placeholder="e.g. Nike, Target"
                      list="coupon-store-suggestions"
                      required
                    />
                  </label>
                  <label className="coupons-field">
                    <span className="coupons-label">Store website (optional)</span>
                    <input
                      className="coupons-input"
                      value={form.store_domain}
                      onChange={(e) => setForm((f) => ({ ...f, store_domain: e.target.value }))}
                      placeholder="nike.com"
                    />
                  </label>
                  <label className="coupons-field">
                    <span className="coupons-label">Coupon code *</span>
                    <input
                      className="coupons-input"
                      value={form.code}
                      onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                      placeholder="SAVE20"
                      required
                      autoComplete="off"
                    />
                  </label>
                  <label className="coupons-field">
                    <span className="coupons-label">Discount (optional)</span>
                    <input
                      className="coupons-input"
                      value={form.discount_label}
                      onChange={(e) => setForm((f) => ({ ...f, discount_label: e.target.value }))}
                      placeholder="20% off $50+"
                    />
                  </label>
                  <label className="coupons-field sm:col-span-2">
                    <span className="coupons-label">Notes / terms (optional)</span>
                    <input
                      className="coupons-input"
                      value={form.fine_print}
                      onChange={(e) => setForm((f) => ({ ...f, fine_print: e.target.value }))}
                      placeholder="First-time customers only, excludes sale items…"
                    />
                  </label>
                  <label className="coupons-field">
                    <span className="coupons-label">Expires (optional)</span>
                    <input
                      className="coupons-input"
                      type="date"
                      value={form.expires_at}
                      onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                    />
                  </label>
                </div>
                <datalist id="coupon-store-suggestions">
                  {storeOptions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                <div className="coupons-form-actions">
                  <button type="submit" className="coupons-btn-primary" disabled={saving}>
                    {saving ? "Saving…" : editingId != null ? "Update coupon" : "Save coupon"}
                  </button>
                  {editingId != null ? (
                    <button type="button" className="coupons-btn-ghost" onClick={cancelEdit}>
                      Cancel edit
                    </button>
                  ) : null}
                </div>
              </form>
            </section>

            <div className="coupons-toolbar">
              <label className="coupons-field max-w-md">
                <span className="coupons-label">Search your codes</span>
                <input
                  className="coupons-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Code, store, or discount…"
                />
              </label>
              <label className="coupons-field max-w-xs">
                <span className="coupons-label">Filter by store</span>
                <select
                  className="coupons-input"
                  value={filterStore}
                  onChange={(e) => setFilterStore(e.target.value)}
                >
                  <option value="">All stores</option>
                  {storeOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {loading ? (
              <p className="coupons-muted">Loading your coupons…</p>
            ) : coupons.length === 0 ? (
              <p className="coupons-empty-inline">
                You haven’t saved any private codes yet. Add one above, or copy from Collected for you.
              </p>
            ) : (
              <ul className="coupons-list">
                {coupons.map((c) => {
                  const expired = isExpired(c.expires_at);
                  return (
                    <li
                      key={c.coupon_id}
                      className={`coupons-card${expired ? " coupons-card-expired" : ""}`}
                    >
                      <span className="coupons-card-store">{c.store_name}</span>
                      {c.store_domain ? (
                        <span className="text-xs text-slate-500">{c.store_domain}</span>
                      ) : null}
                      <p className="coupons-card-code">{c.code}</p>
                      {c.discount_label ? (
                        <p className="coupons-card-discount">{c.discount_label}</p>
                      ) : null}
                      {c.fine_print ? (
                        <p className="mt-2 text-xs leading-relaxed text-slate-600">{c.fine_print}</p>
                      ) : null}
                      <div className="coupons-card-meta">
                        {c.expires_at ? (
                          <span className={expired ? "font-semibold text-red-600" : ""}>
                            {expired ? "Expired " : "Expires "}
                            {formatExpiry(c.expires_at)}
                          </span>
                        ) : (
                          <span>No expiry set</span>
                        )}
                      </div>
                      <div className="coupons-card-actions">
                        <button
                          type="button"
                          className="coupons-icon-btn"
                          aria-label="Copy this promo code to your clipboard"
                          onClick={() =>
                            copyCode(c.coupon_id, c.code, {
                              store_name: c.store_name,
                              source: "saved",
                            })
                          }
                        >
                          <LuCopy size={14} aria-hidden />
                          {copiedId === c.coupon_id ? "Copied to clipboard" : "Copy to clipboard"}
                        </button>
                        <button
                          type="button"
                          className="coupons-icon-btn"
                          onClick={() => startEdit(c)}
                        >
                          <LuPencil size={14} aria-hidden />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="coupons-icon-btn border-red-100 text-red-700 hover:bg-red-50"
                          onClick={() => remove(c.coupon_id)}
                        >
                          <LuTrash2 size={14} aria-hidden />
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </details>
      </main>
    </div>
  );
}
